import { useMutation, useQuery } from "@apollo/client";
import { useIonPopover } from "@ionic/react";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Button from "@components/buttons/Button";
import ClientAvatar from "@components/ClientAvatar";
import styles from "@components/ProfileDropdown.module.css";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import ExternalLink from "@components/typography/ExternalLink";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import { OnboardingStepEnum } from "@typing/Enums";
import {
  ClientRoleRoleEnum,
  GetClientWithJourneysDocument,
  GetClientWithJourneysQuery,
  GetClientWithJourneysQueryVariables,
  JourneyCreateDocument,
  JourneyCreateMutation,
  JourneyCreateMutationVariables
} from "@typing/Generated";
import { getCareProfileName, splitLOName } from "@utils/modelUtils";
import { isIosNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type AccountPopoverProps = {
  client: GetClientWithJourneysQuery["client"] | undefined;
  closeMenu: () => void;
  handleJourneyCreate: () => void;
  handleJourneyIdSelect: (id: string) => void;
  journeyId: string;
};

const AccountPopover = ({
  client,
  closeMenu,
  handleJourneyCreate,
  handleJourneyIdSelect,
  journeyId
}: AccountPopoverProps) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const clientHasUnattachedCarePlanningPackage = useMemo(
    () => client?.eligiblePackageGrants.some(grant => grant.hasCarePlanning && !grant.carePlanningJourneyId),
    [client?.eligiblePackageGrants]
  );

  return (
    <div className={styles.popover}>
      <div className={styles.title}>
        <BodyCopy className={styles.nameSpacing} color="gray-500" size="small">
          <FormattedMessage id="pages.client.header.journey" />
        </BodyCopy>
      </div>

      {client?.roles
        .filter(role => role.role !== ClientRoleRoleEnum.OWNER || myExpert || (myClient && client.everEligible))
        .map(role => (
          <ButtonLink
            className={`${styles.item} ${styles.journeyGroup}`}
            data-test="role"
            key={role.id}
            onClick={() => handleJourneyIdSelect(role.journey.id)}
            stylized={false}
          >
            <div className={styles.journeyInfo}>
              <div className={styles.avatarSpacing}>
                <ClientAvatar
                  clientId={null}
                  color="primary-500"
                  journeyId={journeyId}
                  {...splitLOName(role.journey.careProfile.name)}
                />
              </div>
              <div className={styles.wrapText}>
                <BodyCopy className={styles.nameSpacing} color="gray-500" size="small">
                  {getCareProfileName({ intl, journey: role.journey })}
                </BodyCopy>
              </div>
            </div>
            {role.journey.id === journeyId && (
              <div className={styles.end}>
                <Icon className={styles.check} icon="checkmark" />
              </div>
            )}
          </ButtonLink>
        ))}
      {client && (client.hasUnlimitedMinutes || client.timeBalance > 0 || clientHasUnattachedCarePlanningPackage) ? (
        <ButtonLink
          className={`${styles.item} ${styles.journeyGroup}`}
          data-test="add"
          onClick={handleJourneyCreate}
          stylized={false}
        >
          <div className={styles.addJourney}>
            <Icon color="primary" icon="plus" spacingEnd />
            <BodyCopy className={styles.nameSpacing} color="gray-500" size="small">
              <FormattedMessage id="pages.client.account.addAJourney" />
            </BodyCopy>
          </div>
        </ButtonLink>
      ) : (
        <>
          {myExpert ? (
            <div className={styles.zeroBalance}>
              <FormattedMessage id="pages.client.account.expertZeroBalanceNoNewJourney" />
            </div>
          ) : (
            <>
              <div className={styles.zeroBalance}>
                <FormattedMessage id="pages.client.account.zeroBalanceNoNewJourney" />
              </div>
              <div className={styles.zeroBalance}>
                <FormattedMessage id="pages.client.account.zeroBalanceCreateJourney" />
              </div>
              {!isIosNativeApp() && (
                <div className={styles.zeroBalanceButton}>
                  <Button
                    color="secondary-500"
                    routerLink={journeyRoute({
                      journeyId,
                      linkAfterJourney: "/account?section=package",
                      myExpert,
                      preventScroll: false
                    })}
                  >
                    <FormattedMessage id="pages.client.account.goToMyAccount" />
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
      <div className={styles.accountSettings}>
        <InternalLink
          className={styles.fullWidth}
          onClick={closeMenu}
          stylized={false}
          to={journeyRoute({ journeyId, linkAfterJourney: "/account", myExpert, preventScroll: false })}
        >
          <div className={styles.accountSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="pages.client.account.accountDetails" />
            </BodyCopy>
          </div>
        </InternalLink>
        <InternalLink className={styles.fullWidth} onClick={closeMenu} stylized={false} to="/sign-out">
          <div className={styles.accountSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="dictionary.signOut" />
            </BodyCopy>
          </div>
        </InternalLink>
      </div>
      <div className={styles.otherSettings}>
        <ExternalLink
          className={styles.fullWidth}
          href="https://www.withgrayce.com/terms"
          onClick={closeMenu}
          stylized={false}
        >
          <div className={styles.otherSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="pages.client.account.termsOfUse" />
            </BodyCopy>
          </div>
        </ExternalLink>
        <InternalLink className={styles.fullWidth} onClick={closeMenu} stylized={false} to="/cookie-policy">
          <div className={styles.otherSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="menus.client.cookiePolicy" />
            </BodyCopy>
          </div>
        </InternalLink>
        <ExternalLink
          className={styles.fullWidth}
          href="mailto:support@withgrayce.com"
          onClick={closeMenu}
          stylized={false}
        >
          <div className={styles.otherSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="pages.client.account.contact" />
            </BodyCopy>
          </div>
        </ExternalLink>
        <ExternalLink
          className={styles.fullWidth}
          href="https://www.withgrayce.com/faq"
          onClick={closeMenu}
          stylized={false}
        >
          <div className={styles.otherSetting}>
            <BodyCopy color="gray-500" size="small">
              <FormattedMessage id="pages.client.account.faq" />
            </BodyCopy>
          </div>
        </ExternalLink>
      </div>
    </div>
  );
};

type Props = {
  clientId: string;
  journeyId: string;
};

const ProfileDropdown = ({ clientId, journeyId }: Props) => {
  const history = useHistory();
  const myClient = useMyClient();
  const myExpert = useMyExpert();

  const getClientWithJourneys = useQuery<GetClientWithJourneysQuery, GetClientWithJourneysQueryVariables>(
    GetClientWithJourneysDocument,
    {
      variables: {
        id: clientId
      }
    }
  );

  const [journeyCreate] = useMutation<JourneyCreateMutation, JourneyCreateMutationVariables>(JourneyCreateDocument);

  const handleJourneyCreate = useCallback(() => {
    journeyCreate({
      variables: {
        clientId
      }
    }).then(response => {
      const journey = response.data?.journeyCreate?.journey;
      if (journey && myClient) {
        history.push(`/onboarding/journeys/${journey.id}/profile-questions/${OnboardingStepEnum.USE_CASE_PREFERENCES}`);
      }
      if (journey && myExpert) {
        history.push(`/expert/journeys/${journey.id}/care-profile`);
      }
    });
  }, [clientId, history, journeyCreate, myClient, myExpert]);

  const handleJourneyIdSelect = useCallback(
    (value: string) => {
      history.push(journeyRoute({ journeyId: value, linkAfterJourney: "/care-plan", myClient }));
    },
    [history, myClient]
  );

  const [presentPopover, dismissPopover] = useIonPopover(
    <AccountPopover
      client={getClientWithJourneys.data?.client}
      closeMenu={() => dismissPopover()}
      handleJourneyCreate={handleJourneyCreate}
      handleJourneyIdSelect={id => {
        handleJourneyIdSelect(id);
        dismissPopover();
      }}
      journeyId={journeyId}
    />
  );

  return (
    <div className={styles.header}>
      <div
        className={styles.profileBox}
        onClick={e =>
          presentPopover({ cssClass: styles.popoverClass, event: e.nativeEvent, showBackdrop: false, size: "auto" })
        }
      >
        {(myClient || getClientWithJourneys.data?.client) && (
          <div className={styles.avatarSpacing}>
            <ClientAvatar
              clientId={myClient?.id ?? (getClientWithJourneys.data?.client.id as string)}
              firstName={myClient?.firstName ?? (getClientWithJourneys.data?.client.firstName as string)}
              journeyId={journeyId}
              lastName={myClient?.lastName ?? (getClientWithJourneys.data?.client.lastName as string)}
            />
          </div>
        )}
        <Icon color="primary" icon="chevronDown" size="small" />
      </div>
    </div>
  );
};

export default ProfileDropdown;
